import { faCalendarDay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { memo, useContext, useMemo } from 'react';

import { ActivityLogEvent } from '@/constants/activityLogEvent';
import { Color } from '@/constants/color';
import PersonContext from '@/pages/Person/PersonContext';
import TimelineHeader from '@/pages/Timeline/components/TimelineHeader/TimelineHeader';
import TimestampAndActor from '@/pages/Timeline/components/TimestampAndActor';
import { TimelineSubcomponentProps } from '@/pages/Timeline/types';
import Badge from '@/ui/components/Badge/Badge';
import Card from '@/ui/components/Card';
import DualTime from '@/ui/components/DualTime/DualTime';
import Heading from '@/ui/components/Heading/Heading';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';

// const logdataExample = {
//   backfill: false,
//   attendance_taken_at_datetime: '2023-06-13T22:56:25.760+00:00',
//   status: 'attended',
//   appt_type: {
//     autoCancel: false,
//     clinical: false,
//     counselor1Vs1: false,
//     deletedAt: null,
//     drugScreenExpected: true,
//     groupMeeting: false,
//     initialCounselor1Vs1: false,
//     isActive: true,
//     isContinuedIntake: false,
//     isInitialMedicalCheck: false,
//     isIntake: false,
//     isMedicalCheck: false,
//     maybeBillable: true,
//     name: 'Counselor 1:1',
//     programAdherence: false
//   },
//   appt: {
//     actualDuration: null,
//     clinical: false,
//     confirmedByPerson: null,
//     confirmedByUser: null,
//     contactType: null,
//     drugScreenExpected: true,
//     endDatetime: '2023-06-13T23:00:00.000+00:00',
//     groupMeeting: false,
//     isRecurringAppointment: false,
//     maxAttendees: null,
//     programAdherence: false,
//     providers: {
//     "0": {
//         "preferredName": "First",
//         "email": "example@affecttherapeutics.com",
//         "createdDatetime": "2022-05-06T15:18:23.559+00:00",
//         "updatedDatetime": "2023-06-21T20:58:07.572+00:00",
//         "createdAt": "2022-05-06T15:18:23.559147Z",
//         "updatedAt": "2023-06-21T20:58:07.572207Z",
//         "deletedAt": null,
//         "firstName": "First",
//         "lastName": "Name",
//         "nickname": null,
//         "timeZone": "America/Chicago",
//         "frontId": null,
//         "npi": null,
//         "credentials": null,
//         "role": null,
//         "licensedIn": [],
//         "providerAppointmentLocations": []
//     },
//     reason: null,
//     startDatetime: '2023-06-13T22:00:00.000+00:00',
//     title: 'Counselor 1:1',
//     initiator: {
//       credentials: '',
//       deletedAt: null,
//       firstName: '',
//       frontId: '',
//       lastName: '',
//       licensedIn: [],
//       nickname: '',
//       npi: '',
//       role: null,
//       timeZone: ''
//     }
//   }
// };

const AppointmentAttendanceEvent = ({ activityLog }: TimelineSubcomponentProps): JSX.Element => {
  const {
    person: { time_zone }
  } = useContext(PersonContext);
  const { action, actor, data, timestamp } = activityLog;
  const providersEntries = data?.appt?.providers ? Object.entries(data?.appt?.providers) : [];

  const title = useMemo(() => {
    switch (action) {
      case ActivityLogEvent.appointment_canceled:
      case ActivityLogEvent.APPOINTMENT_CANCELED_EVENT:
        return 'Appointment Canceled';

      case ActivityLogEvent.appointment_confirmed:
      case ActivityLogEvent.APPOINTMENT_CONFIRMED_EVENT:
        return 'Appointment Confirmed';

      case ActivityLogEvent.appointment_scheduled:
      case ActivityLogEvent.APPOINTMENT_SCHEDULED_EVENT:
        return 'Appointment Scheduled';

      case ActivityLogEvent.appointment_status:
      case ActivityLogEvent.APPOINTMENT_STATUS_EVENT:
        return `Appointment ${snakeCaseToTitleCase(data?.status)}`;
      default:
        return action ?? '';
    }
  }, [action, data?.status]);

  return (
    <div>
      <Card>
        <TimelineHeader icon={faCalendarDay} title={title} />

        <div className="mt-1 p-2 border border-gray-200 rounded-lg">
          {data?.appt?.title && (
            <div className="flex items-center border-b border-gray-200 mb-1 pb-2">
              <FontAwesomeIcon icon={faCalendarDay} className="mr-2" />
              <Heading text={data.appt.title} value={4} className="text-lg font-normal" />
            </div>
          )}

          <div className="mb-1 space-x-1">
            {!!providersEntries.length && (
              <>
                {providersEntries.map((entry, index) => {
                  const provider = entry[1] as unknown as { firstName: string; lastName: string };

                  return (
                    <Badge
                      color={Color.GRAY}
                      key={`${provider?.firstName}${provider.lastName}${index}`}>
                      <>
                        Provider: {provider?.firstName && provider.firstName}{' '}
                        {provider?.lastName && provider.lastName}
                      </>
                    </Badge>
                  );
                })}
              </>
            )}
          </div>

          {data?.appt?.startDatetime && (
            <Badge color={Color.GRAY}>
              <span className="pre-wrap">
                Start At:{' '}
                <DualTime dateTime={new Date(data.appt.startDatetime)} memberTZ={time_zone} />
              </span>
            </Badge>
          )}
        </div>
      </Card>

      <TimestampAndActor actor={actor} timestamp={timestamp} />
    </div>
  );
};

export default memo(AppointmentAttendanceEvent);
